import React, { useContext, useRef } from 'react';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { SegmentationContext } from '@/contexts/SegmentationContext';
import { useGetTranslations } from '@/generated/translations';
import styles from './Map.module.scss';
import { mapOptions } from './mapOptions';

interface MapProps {
	latitude?: number;
	longitude?: number;
	apiKey: string;
	zoom?: number;
	onDragEnd?: (lat: string, lng: string) => void;
}

const markerIcon =
	'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyMCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggaWQ9ImxvY2F0aW9uX29uIiBkPSJNMTAuMDA2NyAxMkMxMC42NDMzIDEyIDExLjE4NTkgMTEuNzc5MSAxMS42MzQ2IDExLjMzNzJDMTIuMDgzMyAxMC44OTUzIDEyLjMwNzcgMTAuMzY0MSAxMi4zMDc3IDkuNzQzNDRDMTIuMzA3NyA5LjEyMjgxIDEyLjA4MTEgOC41OTM3NSAxMS42Mjc5IDguMTU2MjVDMTEuMTc0NyA3LjcxODc1IDEwLjYyOTggNy41IDkuOTkzMjcgNy41QzkuMzU2NzMgNy41IDguODE0MSA3LjcyMDk0IDguMzY1MzkgOC4xNjI4MUM3LjkxNjY3IDguNjA0NjkgNy42OTIzMSA5LjEzNTk0IDcuNjkyMzEgOS43NTY1NkM3LjY5MjMxIDEwLjM3NzIgNy45MTg5MSAxMC45MDYyIDguMzcyMTIgMTEuMzQzOEM4LjgyNTMyIDExLjc4MTIgOS4zNzAxOSAxMiAxMC4wMDY3IDEyWk0xMCAyNEM2LjY0NTk0IDIxLjI2ODUgNC4xNDA3MSAxOC43MzE2IDIuNDg0MjkgMTYuMzg5MUMwLjgyODA5OCAxNC4wNDY0IDAgMTEuODY0NiAwIDkuODQzNzVDMCA3LjA1MjA4IDAuOTUwODU1IDQuNzEzNTQgMi44NTI1NiAyLjgyODEyQzQuNzU0MjcgMC45NDI3MDggNy4xMzE0MSAwIDkuOTgzOTcgMEMxMi44MzY1IDAgMTUuMjE5IDAuOTQyNzA4IDE3LjEzMTQgMi44MjgxMkMxOS4wNDM4IDQuNzEzNTQgMjAgNy4wNTIwOCAyMCA5Ljg0Mzc1QzIwIDExLjg2NDYgMTkuMTc3NCAxNC4wNDE3IDE3LjUzMjEgMTYuMzc1QzE1Ljg4NjggMTguNzA4MyAxMy4zNzYxIDIxLjI1IDEwIDI0WiIgZmlsbD0iIzM3OTNBMyIvPgo8L3N2Zz4K';

const Map: React.FC<MapProps> = ({ latitude, longitude, apiKey, zoom, onDragEnd }) => {
	const { segmentationId } = useContext(SegmentationContext);
	const { data: translations } = useGetTranslations(segmentationId);

	const { isLoaded, loadError } = useJsApiLoader({
		googleMapsApiKey: apiKey,
	});

	const MapTools = useRef<google.maps.Map | null>(null);

	const loadInstance = (x: google.maps.Map | null) => {
		MapTools.current = x;
	};

	const getLatLng = (e: google.maps.MapMouseEvent) => {
		onDragEnd && onDragEnd((e.latLng?.lat() || 46.1657).toString(), (e.latLng?.lng() || 10.4515).toString());
		MapTools?.current?.setZoom(17);
	};

	if (loadError) return <div>{translations?.data.customers.map.mapErrorMessage}</div>;
	if (!isLoaded) return <div className={styles.mapSkeleton} />;

	return (
		<GoogleMap
			mapContainerStyle={{
				width: '100%',
				height: '100%',
				backgroundColor: 'var(--color-background-primary-weaker)',
			}}
			center={{ lat: latitude ?? 46.1657, lng: longitude ?? 10.4515 }}
			zoom={zoom || 12}
			options={mapOptions}
			onLoad={loadInstance}
		>
			{latitude && longitude ? (
				<Marker
					icon={markerIcon}
					position={{ lat: latitude, lng: longitude }}
					draggable
					onDragEnd={getLatLng}
				/>
			) : null}
		</GoogleMap>
	);
};

export const MemoizedMap = React.memo(Map);
